import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import gameService from '../../utils/gameService';
import Header from '../../components/ui/Header';
import GameCard from '../game-library-dashboard/components/GameCard';
import LoadingSkeleton from '../game-library-dashboard/components/LoadingSkeleton';
import { Library, Search, Clock, Calendar } from 'lucide-react';

const MyLibrary = () => {
  const [library, setLibrary] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('library'); // 'library' or 'favorites'
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('recent'); // recent, playtime, alphabetical

  const { user } = useAuth();

  useEffect(() => {
    const loadData = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch Library
        const libraryResult = await gameService.getUserGameSessions(user.id);
        if (libraryResult?.success) {
          const gameStats = {};
          libraryResult.data.forEach(session => {
            const gameId = session.game_id;
            if (!gameStats[gameId]) {
              gameStats[gameId] = {
                game: session.games,
                totalPlaytime: 0,
                sessionCount: 0,
                lastPlayed: session.created_at,
                progress: session.progress_percentage || 0
              };
            }
            gameStats[gameId].totalPlaytime += session.duration_minutes || 0;
            gameStats[gameId].sessionCount += 1;
            if (new Date(session.created_at) > new Date(gameStats[gameId].lastPlayed)) {
              gameStats[gameId].lastPlayed = session.created_at;
              gameStats[gameId].progress = session.progress_percentage || 0;
            }
          });
          setLibrary(Object.values(gameStats));
        }

        // Fetch Favorites
        const favoritesResult = await gameService.getUserFavorites(user.id);
        if (favoritesResult?.success) {
          setFavorites(favoritesResult.data || []);
        }

      } catch (err) {
        setError('Failed to load library data');
        console.error('Error loading library:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user]);

  const filteredLibrary = library
    .filter(item =>
      item.game?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.game?.description?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      switch (sortBy) {
        case 'recent':
          return new Date(b.lastPlayed) - new Date(a.lastPlayed);
        case 'playtime':
          return b.totalPlaytime - a.totalPlaytime;
        case 'alphabetical':
          return (a.game?.title || '').localeCompare(b.game?.title || '');
        default:
          return 0;
      }
    });

  const filteredFavorites = favorites.filter(fav =>
    fav.games?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    fav.games?.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatPlaytime = (minutes) => {
    if (!minutes) return '0m';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const totalPlaytime = library.reduce((sum, item) => sum + item.totalPlaytime, 0);

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 flex items-center justify-center h-96">
          <div className="text-center">
            <Library className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-foreground mb-2">Sign In Required</h2>
            <p className="text-muted-foreground">Please sign in to view your library.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-16 pb-20 lg:pb-8">
        <div className="container mx-auto px-4 py-8">

          {/* Header & Tabs */}
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">My Library</h1>
              <div className="flex space-x-6 border-b border-border">
                <button
                  onClick={() => setActiveTab('library')}
                  className={`pb-2 text-sm font-medium transition-colors relative ${activeTab === 'library' ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
                    }`}
                >
                  My Games
                  {activeTab === 'library' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />}
                </button>
                <button
                  onClick={() => setActiveTab('favorites')}
                  className={`pb-2 text-sm font-medium transition-colors relative ${activeTab === 'favorites' ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
                    }`}
                >
                  Favorites
                  {activeTab === 'favorites' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />}
                </button>
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center space-x-4">
              {activeTab === 'library' && library.length > 0 && (
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-2 bg-muted/50 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm"
                >
                  <option value="recent">Recently Played</option>
                  <option value="playtime">Most Played</option>
                  <option value="alphabetical">A-Z</option>
                </select>
              )}

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <input
                  type="text"
                  placeholder={activeTab === 'library' ? "Search library..." : "Search favorites..."}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full md:w-64 pl-10 pr-4 py-2 bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                />
              </div>
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <LoadingSkeleton />
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-destructive mb-4">{error}</div>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
              >
                Try Again
              </button>
            </div>
          ) : (
            <>
              {activeTab === 'library' && (
                <>
                  {library.length === 0 ? (
                    <div className="text-center py-12">
                      <Library className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                      <h2 className="text-2xl font-bold text-foreground mb-2">No Games Played Yet</h2>
                      <p className="text-muted-foreground mb-6">Start playing games to build your library!</p>
                      <a href="/browse" className="inline-flex items-center px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors">
                        Browse Games
                      </a>
                    </div>
                  ) : filteredLibrary.length === 0 ? (
                    <div className="text-center py-12">
                      <Search className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                      <h2 className="text-2xl font-bold text-foreground mb-2">No Results Found</h2>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                      {filteredLibrary.map((item) => (
                        <div key={item.game?.id} className="relative group">
                          <GameCard game={item.game} showFavorite={true} />
                          <div className="absolute top-2 right-2 bg-black/80 text-white text-xs rounded-lg p-2 space-y-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                            <div className="flex items-center space-x-1">
                              <Clock className="w-3 h-3" />
                              <span>{formatPlaytime(item.totalPlaytime)}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Calendar className="w-3 h-3" />
                              <span>{new Date(item.lastPlayed).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {activeTab === 'favorites' && (
                <>
                  {favorites.length === 0 ? (
                    <div className="text-center py-12">
                      <h2 className="text-2xl font-bold text-foreground mb-2">No Favorites Yet</h2>
                      <p className="text-muted-foreground mb-6">Games you favorite will appear here.</p>
                      <a href="/browse" className="inline-flex items-center px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors">
                        Browse Games
                      </a>
                    </div>
                  ) : filteredFavorites.length === 0 ? (
                    <div className="text-center py-12">
                      <Search className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                      <h2 className="text-2xl font-bold text-foreground mb-2">No Results Found</h2>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                      {filteredFavorites.map((fav) => (
                        <GameCard key={fav.id} game={fav.games} showFavorite={true} isFavorite={true} />
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default MyLibrary;
